import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Shield, Copy, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface CreateAdminDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateAdminDialog = ({ isOpen, onClose, onSuccess }: CreateAdminDialogProps) => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate inputs
    if (!name.trim()) {
      toast({
        title: "Invalid name",
        description: "Please enter a valid name",
        variant: "destructive"
      });
      return;
    }

    if (!/^[0-9]{10}$/.test(phone)) {
      toast({
        title: "Invalid phone",
        description: "Please enter a valid 10-digit phone number",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Call auth-provision edge function using Supabase client
      const { data, error } = await supabase.functions.invoke('auth-provision', {
        body: {
          phone: phone,
          name: name.trim(),
          role: 'admin'
        }
      });

      if (error) throw error;

      if (data?.success && data?.tempPassword) {
        setTempPassword(data.tempPassword);
        toast({
          title: "Success",
          description: "Admin account created successfully",
        });
        onSuccess();
      } else {
        throw new Error('Failed to create admin account');
      }
    } catch (error: any) {
      console.error('Error creating admin:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create admin account",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopyPassword = () => {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword);
      toast({
        title: "Copied",
        description: "Password copied to clipboard",
      });
    }
  };

  const handleClose = () => {
    setName("");
    setPhone("");
    setTempPassword(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Create Admin Account
          </DialogTitle>
          <DialogDescription>
            Create a new administrator with full system access
          </DialogDescription>
        </DialogHeader>

        {!tempPassword ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter admin name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isProcessing}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="10-digit phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                disabled={isProcessing}
                required
                maxLength={10}
              />
              <p className="text-xs text-muted-foreground">
                Phone will be used for login authentication
              </p>
            </div>

            <Alert>
              <AlertDescription className="text-xs">
                <strong>Security Note:</strong> A temporary password will be generated. 
                Share it securely with the new admin.
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isProcessing}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isProcessing}
                className="flex-1"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Admin'
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-6 h-6" />
              <h3 className="text-lg font-semibold">Admin Created Successfully</h3>
            </div>

            <div className="space-y-2">
              <Label>Admin Details</Label>
              <div className="p-3 bg-muted rounded-lg space-y-1 text-sm">
                <p><strong>Name:</strong> {name}</p>
                <p><strong>Phone:</strong> {phone}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Temporary Password</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={tempPassword}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopyPassword}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Alert>
              <AlertDescription className="text-xs">
                <strong>Important:</strong> Share this password securely with the admin. 
                They can change it after first login.
              </AlertDescription>
            </Alert>

            <Button onClick={handleClose} className="w-full">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
